import React, { useCallback, useRef, useState, useEffect } from "react";
import { ThreeEvent, useThree } from "@react-three/fiber";
import { useModelStore } from "../../../../store/useModelStore";
import * as THREE from "three";

interface DragSelectionHandlerProps {
  /** Whether selection mode is active (via double-click toggle or menu selection) */
  isSelectionMode: boolean;
  /** Whether Ctrl key is pressed (enables Ctrl+Drag selection) */
  isCtrlPressed: boolean;
  onSelectionMouseDown: (event: React.MouseEvent) => void;
  onSelectionMouseMove: (event: React.MouseEvent) => void;
  onSelectionMouseUp: (event: React.MouseEvent) => void;
  /** Callback when empty space is clicked (for deselection) */
  onEmptySpaceClick?: () => void;
}

// Minimum drag distance in pixels to distinguish drag from click
const MIN_DRAG_DISTANCE = 5;

// Custom crosshair cursor SVG (encoded for CSS)
const CROSSHAIR_CURSOR = `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32"><line x1="16" y1="4" x2="16" y2="28" stroke="%23000" stroke-width="2" stroke-linecap="round"/><line x1="4" y1="16" x2="28" y2="16" stroke="%23000" stroke-width="2" stroke-linecap="round"/><circle cx="16" cy="16" r="2" fill="%23000"/></svg>') 16 16, crosshair`;

/**
 * DragSelectionHandler - Handles drag selection and empty space click detection
 *
 * Features:
 * 1. Ctrl+Drag: Start drag selection immediately - cursor changes to crosshair ONLY during drag
 * 2. Selection Mode: When active (via double-click or menu), enables the filter (nodes/beams/all)
 * 3. Empty Space Click: Clicking empty canvas area - no action (manual selection preserved)
 * 4. Additive Selection: Ctrl+drag adds to existing selection, doesn't replace
 *
 * Cursor Behavior:
 * - Normal cursor at all times EXCEPT during Ctrl+drag
 * - When user presses Ctrl and starts dragging, cursor changes to crosshair
 * - When drag ends (mouse up), cursor returns to normal
 */
export const DragSelectionHandler: React.FC<DragSelectionHandlerProps> = ({
  isSelectionMode,
  isCtrlPressed,
  onSelectionMouseDown,
  onSelectionMouseMove,
  onSelectionMouseUp,
  onEmptySpaceClick,
}) => {
  // Get Three.js renderer to access the canvas
  const modelStore = useModelStore();
  const { gl, camera } = useThree();

  // Track pointer state for distinguishing click vs drag
  const pointerDownPos = useRef<{ x: number; y: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const hasMovedEnough = useRef(false);

  // Track if we disabled camera controls (to re-enable on pointer up)
  const disabledCameraControls = useRef(false);

  // Track if Ctrl was held during pointer down (for cursor management)
  const wasCtrlDrag = useRef(false);

  /**
   * Set cursor to crosshair during Ctrl+drag
   */
  const setCrosshairCursor = useCallback(() => {
    document.body.style.cursor = CROSSHAIR_CURSOR;
    const canvas = gl.domElement;
    if (canvas) {
      canvas.style.cursor = CROSSHAIR_CURSOR;
    }
  }, [gl]);

  /**
   * Reset cursor to default
   */
  const resetCursor = useCallback(() => {
    const modelStore = useModelStore.getState();

    // Check workspace state for node/beam creation modes
    // Note: These are managed in useWorkspaceState, not useModelStore
    const workspaceState = {
      isNodeCreationMode: false,
      isBeamCreationMode: false,
    };

    // Try to get workspace state from callbacks if available
    try {
      const workspaceElement = document.querySelector("[data-workspace-state]");
      if (workspaceElement) {
        const stateData = (workspaceElement as any).__workspaceState;
        if (stateData) {
          workspaceState.isNodeCreationMode =
            stateData.isNodeCreationMode || false;
          workspaceState.isBeamCreationMode =
            stateData.isBeamCreationMode || false;
        }
      }
    } catch (e) {
      // Fallback: check if buttons are pressed
      const nodeButton = Array.from(document.querySelectorAll("button")).find(
        (btn) =>
          btn.textContent?.includes("Node") &&
          btn.getAttribute("aria-pressed") === "true"
      );
      const beamButton = Array.from(document.querySelectorAll("button")).find(
        (btn) =>
          btn.textContent?.includes("Beam") &&
          btn.getAttribute("aria-pressed") === "true"
      );

      workspaceState.isNodeCreationMode = !!nodeButton;
      workspaceState.isBeamCreationMode = !!beamButton;
    }

    const canvas = gl.domElement;

    // Priority: Node > Beam > Prep > Divide
    if (workspaceState.isNodeCreationMode) {
      canvas.style.cursor = `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32"><circle cx="16" cy="16" r="8" fill="none" stroke="black" stroke-width="2"/><circle cx="16" cy="16" r="1.5" fill="black"/></svg>') 16 16, auto`;
      return;
    }

    if (workspaceState.isBeamCreationMode || modelStore.isBeamCreationMode) {
      canvas.style.cursor = `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32"><path d="M16 6 L26 16 L16 26 L6 16 Z" fill="none" stroke="black" stroke-width="2"/><circle cx="16" cy="16" r="1.5" fill="black"/></svg>') 16 16, auto`;
      return;
    }

    if (modelStore.isPrepMode) {
      canvas.style.cursor = `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32"><line x1="16" y1="4" x2="16" y2="16" stroke="black" stroke-width="2"/><line x1="4" y1="16" x2="28" y2="16" stroke="black" stroke-width="2"/><circle cx="16" cy="16" r="3" fill="white" stroke="black" stroke-width="1"/></svg>') 16 16, auto`;
      return;
    }

    if (modelStore.isDivideBeamMode) {
      canvas.style.cursor = `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32"><circle cx="16" cy="16" r="8" fill="none" stroke="black" stroke-width="2"/><circle cx="16" cy="16" r="1.5" fill="black"/><line x1="2" y1="16" x2="8" y2="16" stroke="black" stroke-width="1.8" stroke-linecap="round"/><line x1="24" y1="16" x2="30" y2="16" stroke="black" stroke-width="1.8" stroke-linecap="round"/></svg>') 16 16, auto`;
      return;
    }

    // No modeling mode active - default cursor
    document.body.style.cursor = "auto";
    canvas.style.cursor = "auto";
  }, [gl]);

  /**
   * Converts Three.js pointer coordinates to screen coordinates
   */
  const convertToScreenCoords = useCallback(
    (event: ThreeEvent<PointerEvent>) => {
      const canvas = gl.domElement;
      const rect = canvas.getBoundingClientRect();
      const x = ((event.pointer.x + 1) / 2) * rect.width;
      const y = ((-event.pointer.y + 1) / 2) * rect.height;
      return { x: rect.left + x, y: rect.top + y, rect, canvas };
    },
    [gl]
  );

  /**
   * Creates a mock React.MouseEvent from Three.js event data
   */
  const createMockEvent = useCallback(
    (
      screenX: number,
      screenY: number,
      canvas: HTMLCanvasElement,
      rect: DOMRect,
      threeEvent: ThreeEvent<PointerEvent>
    ): React.MouseEvent => {
      return {
        clientX: screenX,
        clientY: screenY,
        target: canvas,
        currentTarget: canvas,
        button: 0,
        ctrlKey: threeEvent.nativeEvent.ctrlKey,
        metaKey: threeEvent.nativeEvent.metaKey,
        shiftKey: threeEvent.nativeEvent.shiftKey,
        preventDefault: () => {},
        stopPropagation: () => threeEvent.stopPropagation(),
        getBoundingClientRect: () => rect,
      } as unknown as React.MouseEvent;
    },
    []
  );

  /**
   * Handle pointer down - start potential drag selection
   * ONLY activates on Ctrl+left click drag
   */
  const handlePointerDown = useCallback(
    (event: ThreeEvent<PointerEvent>) => {
      const isCtrl = event.nativeEvent.ctrlKey || event.nativeEvent.metaKey;

      // ONLY allow drag selection when Ctrl is held
      // Selection mode from menu just determines the filter (nodes/beams/all)
      const canStartSelection = isCtrl;

      const { x, y, rect, canvas } = convertToScreenCoords(event);

      // Always track pointer position for click vs drag detection
      pointerDownPos.current = { x, y };
      hasMovedEnough.current = false;
      wasCtrlDrag.current = isCtrl;

      if (canStartSelection) {
        // Disable camera controls when starting Ctrl+drag selection
        // This prevents pan/rotate conflicts in isometric view
        window.dispatchEvent(
          new CustomEvent("disable-camera-controls", {
            detail: { disable: true },
          })
        );
        disabledCameraControls.current = true;

        // Set crosshair cursor during drag
        setCrosshairCursor();

        setIsDragging(true);
        const mockEvent = createMockEvent(x, y, canvas, rect, event);
        onSelectionMouseDown(mockEvent);
      }
    },
    [
      convertToScreenCoords,
      createMockEvent,
      onSelectionMouseDown,
      setCrosshairCursor,
    ]
  );

  /**
   * Handle pointer move - update selection rectangle if dragging
   */
  const handlePointerMove = useCallback(
    (event: ThreeEvent<PointerEvent>) => {
      if (!isDragging && !pointerDownPos.current) return;

      const { x, y, rect, canvas } = convertToScreenCoords(event);

      // Check if moved enough to be considered a drag
      if (pointerDownPos.current && !hasMovedEnough.current) {
        const dx = x - pointerDownPos.current.x;
        const dy = y - pointerDownPos.current.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance >= MIN_DRAG_DISTANCE) {
          hasMovedEnough.current = true;
        }
      }

      if (isDragging) {
        const mockEvent = createMockEvent(x, y, canvas, rect, event);
        onSelectionMouseMove(mockEvent);
      }
    },
    [isDragging, convertToScreenCoords, createMockEvent, onSelectionMouseMove]
  );

  /**
   * Handle pointer up - finalize selection
   * Empty space click does NOT clear selection (user can manually select/deselect)
   */
  const handlePointerUp = useCallback(
    (event: ThreeEvent<PointerEvent>) => {
      const { x, y, rect, canvas } = convertToScreenCoords(event);

      if (isDragging) {
        // Finalize drag selection
        const mockEvent = createMockEvent(x, y, canvas, rect, event);
        onSelectionMouseUp(mockEvent);
        setIsDragging(false);
      }
      // NOTE: Clicking empty space no longer clears selection
      // User must use Clear button or select different elements

      // Re-enable camera controls if we disabled them
      if (disabledCameraControls.current) {
        window.dispatchEvent(
          new CustomEvent("disable-camera-controls", {
            detail: { disable: false },
          })
        );
        disabledCameraControls.current = false;
      }

      // Reset cursor to normal after Ctrl+drag
      if (wasCtrlDrag.current) {
        resetCursor();
        wasCtrlDrag.current = false;
      }

      // Reset tracking state
      pointerDownPos.current = null;
      hasMovedEnough.current = false;
    },
    [
      isDragging,
      convertToScreenCoords,
      createMockEvent,
      onSelectionMouseUp,
      resetCursor,
    ]
  );

  // Always render the invisible plane to capture Ctrl+drag events
  return (
    <>
      {/* XY Plane (catches rays from Z axis - Top/Isometric) */}
      <mesh
        position={[0, 0, 0]}
        rotation={[0, 0, 0]}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        visible={false}
      >
        <planeGeometry args={[100000, 100000]} />
        <meshBasicMaterial transparent opacity={0} side={THREE.DoubleSide} />
      </mesh>

      {/* XZ Plane (catches rays from Y axis - Front view) */}
      <mesh
        position={[0, 0, 0]}
        rotation={[Math.PI / 2, 0, 0]}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        visible={false}
      >
        <planeGeometry args={[100000, 100000]} />
        <meshBasicMaterial transparent opacity={0} side={THREE.DoubleSide} />
      </mesh>

      {/* YZ Plane (catches rays from X axis - Side view) */}
      <mesh
        position={[0, 0, 0]}
        rotation={[0, Math.PI / 2, 0]}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        visible={false}
      >
        <planeGeometry args={[100000, 100000]} />
        <meshBasicMaterial transparent opacity={0} side={THREE.DoubleSide} />
      </mesh>
    </>
  );
};
